import clsx from 'clsx';
import { useRef, useState, useEffect, useCallback } from 'react';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useEffectOnce } from 'react-use';
import { useFirestore } from 'reactfire';
import Signup from '@/features/Auth/Signup';
import { useToast } from '@/hooks/useToast';
import navConfig from '@/config/navigation';
import generalConfig from '@/config/general';
import account from '@/services/auth/account';
import SEO from '@/components/head/Seo';
import useTeam from '@/hooks/useTeam';
import ClientOnly from '@/components/ClientOnly';
import getQueryParams from '@/utils/getQueryParams';
import Nav from '@/components/navigation/Public';
import Footer from '@/components/legal/Footer';
import layoutStyles from '@/components/Layout/styles.module.scss';

const SignUpPage: NextPage = () => {
  const router = useRouter();
  const firestore = useFirestore();
  const { addToast } = useToast();
  const [userId, setUserId] = useState('');
  const hasRedirected = useRef(false);
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const { teamId, email, redirect } = getQueryParams(router.query);
  const {
    status: teamStatus,
    data: team,
    error: teamError,
  } = useTeam(firestore, teamId || 'undefined');
  const isLoading = Boolean(teamId) && teamStatus === 'loading';

  // Use window redirect to avoid
  // user auth race condition on signup
  const redirectUser = useCallback(
    (dest = navConfig.defaultPrivatePage) => {
      if (hasRedirected.current) return; // only redirect once
      const finalDest = !redirect ? dest : `${dest}?redirect=${redirect}`;

      if (typeof window !== 'undefined') {
        window.location.href = finalDest;
      } else {
        router.push(finalDest);
      }

      hasRedirected.current = true;
    },
    [router, redirect]
  );

  // Subscribe to session changes
  useEffectOnce(() => {
    // Return unsubscribe
    return account.onAuthStateChanged((user) => {
      setUserId(user?.uid || '');
    });
  });

  // Automatically redirect logged in user
  // NOTE: user with session + db record
  useEffect(() => {
    if (!userId) return;

    // Return unsubscribe
    return account.onUserRecordChange(userId, (user) => {
      setHasActiveSession(Boolean(user));
      if (user) redirectUser();
    });
  }, [userId, redirectUser]);

  useEffect(() => {
    if (teamError) {
      addToast({
        description:
          'Requested team failed to load, please try refreshing or contacting the account owner',
        type: 'error',
      });
    }
  }, [teamError, addToast]);

  return (
    <>
      <SEO
        title="Sign Up"
        description={`${generalConfig.productName} Sign Up`}
      />
      <div role="main" className={clsx(layoutStyles.main, '-bgc-white')}>
        <Nav />
        <ClientOnly>
          {isLoading ? (
            <div data-status={teamStatus}>Loading</div>
          ) : (
            <Signup
              team={team}
              email={email}
              redirect={redirect}
              onGoogleAuthSuccess={() =>
                // Allow user to read any notifications
                setTimeout(() => redirectUser('/login'), 1000)
              }
              hasActiveSession={hasActiveSession}
            />
          )}
        </ClientOnly>
        <Footer />
      </div>
    </>
  );
};

export default SignUpPage;
